import { partition } from 'lodash';

import { fetchScriptStateDetail, updateScriptTitle } from '@/api/apiService';
import { listLanguage } from '@/static/languages';
import { useToastStore } from '@/stores/toast';
import { LanguageCode, TypeCall } from '@/types/common';

export default function useChatScriptDetail(
  updateScript: (chatId: string, updatedScript: Partial<ChatScript>) => void
) {
  const { t } = useI18n();

  const toastStore = useToastStore();

  const titleModalId = ref<string>('');

  const getChatTitle = (chatType: TypeCall): string => {
    return (
      {
        GENERAL: t('label_general'),
        CONFERENCE: t('label_conference'),
        MEETING: t('label_meeting'),
        AUDIENCE: t('label_audience'),
        SINGLE: t('label_single'),
      }[chatType] || t('label_interpretation')
    );
  };

  const toggleTitleModal = (chatId: string) => {
    if (chatId === titleModalId.value) {
      titleModalId.value = '';
    } else {
      titleModalId.value = chatId;
    }
  };

  const getSortedLanguages = (code: LanguageCode) => {
    const inx = listLanguage.findIndex((item) => item.code === code);

    if (inx <= 0) {
      return listLanguage;
    } else {
      const [selectedLanguage, otherLanguages] = partition(listLanguage, { code });
      return [...selectedLanguage, ...otherLanguages];
    }
  };

  const isValidTitle = (title: string): boolean => {
    return !title || title.length <= 30;
  };

  /**
   * @summary 언어코드별 스크립트 번역 상태 조회
   * @param {string} chatId
   * @param {LanguageCode} languageCode
   * */
  const requestScriptState = async (chatId: string, languageCode: LanguageCode) => {
    try {
      const res = await fetchScriptStateDetail(chatId, { languageCode });
      if (!res.success) {
        throw new Error();
      }
      updateScript(chatId, { status: res.data.status, languageCode });
    } catch {
      toastStore.error(t('toast_request_failed'));
    }
  };

  /**
   * @summary 스크립트 요약 정보 수정 저장
   * @param {string} chatId
   * @param {string} title
   * */
  const requestChangeScriptTitle = async (chatId: string, title: string) => {
    if (!isValidTitle(title)) {
      return;
    }

    try {
      const res = await updateScriptTitle(chatId, { title });
      if (!res.success) {
        throw new Error();
      }
      updateScript(chatId, { title: res.data });
    } catch {
      toastStore.error(t('toast_request_failed'));
    }
  };

  return {
    titleModalId,
    getChatTitle,
    toggleTitleModal,
    getSortedLanguages,
    requestScriptState,
    requestChangeScriptTitle,
  };
}
